import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../services/api";

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
};

const setStoredUser = (user) => {
  localStorage.setItem("user", JSON.stringify(user));
};

export default function AccountSettings() {
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);

  const initialForm = useMemo(
    () => ({
      name: user?.name || "",
      email: user?.email || "",
    }),
    [user?.email, user?.name]
  );

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  useEffect(() => {
    const refresh = async () => {
      try {
        const res = await api.get("/auth/me");
        const current = res.data?.user;
        if (current) {
          setUser(current);
          setStoredUser(current);
        }
      } catch {
        // Best-effort only; keep local snapshot.
      }
    };

    refresh();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required.");

    const toastId = toast.loading("Saving profile...");
    setLoading(true);
    try {
      const res = await api.put("/users/me", { name: form.name.trim(), email: form.email.trim().toLowerCase() });
      const nextUser = res.data?.user || { ...(user || {}), name: form.name.trim(), email: form.email.trim() };
      setUser(nextUser);
      setStoredUser(nextUser);
      toast.success("Profile updated.", { id: toastId });
      setEditing(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) toast.error("Profile updates aren’t enabled on the server yet.", { id: toastId });
      else toast.error(err?.response?.data?.message || "Failed to update profile.", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-gray-500 dark:text-gray-400">Role</p>
          <p className="font-semibold text-gray-800 dark:text-gray-100">{user?.role || "—"}</p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-gray-400">District</p>
          <p className="font-semibold text-gray-800 dark:text-gray-100">{user?.district || "—"}</p>
        </div>
      </div>

      {!editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500 dark:text-gray-400">Name</p>
              <p className="font-semibold text-gray-800 dark:text-gray-100">{user?.name || "—"}</p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400">Email</p>
              <p className="font-semibold text-gray-800 dark:text-gray-100">{user?.email || "—"}</p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              className="text-xs px-4 py-2 rounded text-white font-semibold bg-green-700 hover:bg-green-800"
              onClick={() => setEditing(true)}
            >
              Edit profile
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              className="border p-2 rounded w-full focus:ring-2 focus:ring-green-600 outline-none bg-white dark:bg-gray-900 dark:border-gray-700"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              className="border p-2 rounded w-full focus:ring-2 focus:ring-green-600 outline-none bg-white dark:bg-gray-900 dark:border-gray-700"
              placeholder="you@example.com"
            />
          </div>

          <div className="md:col-span-2 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Changes update your account in the database.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-xs px-3 py-2 rounded border hover:bg-gray-50 dark:hover:bg-gray-700/40 dark:border-gray-700"
                onClick={() => {
                  setForm(initialForm);
                  setEditing(false);
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`text-xs px-3 py-2 rounded text-white font-semibold ${
                  loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-700 hover:bg-green-800"
                }`}
              >
                {loading ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
